import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, User, Video, Radio, Shield, MessageCircle, Music, UserCircle } from 'lucide-react';
import NotificationBell from './NotificationBell';

const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentLive, setCurrentLive] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    fetchCurrentLive();
    const interval = setInterval(fetchCurrentLive, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchCurrentLive = async () => {
    try {
      const res = await api.get('/live-streaming/current-public');
      setCurrentLive(res.data.current_live);
    } catch (error) {
      console.error('Error fetching current live:', error);
    }
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  return (
    <header className="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo AGRP BOX */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/logo.png" 
              alt="AGRP BOX Logo" 
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1"
            >
              <Video size={18} />
              <span>Boutique</span>
            </Link>
            {user && (
              <Link
                to="/dashboard"
                className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1"
              >
                <span className="text-lg">🎯</span>
                <span>Mes Services</span>
              </Link>
            )}
            
            {/* Badge LIVE en haut */}
            {currentLive && (
              <a
                href={currentLive.youtube_link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-red-600 text-white font-bold px-3 py-1.5 rounded animate-pulse hover:bg-red-700 transition-colors"
                title={currentLive.video_title}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                🔴 LIVE
              </a>
            )}
            
            <Link
              to="/live"
              className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1"
            >
              <Radio size={18} />
              <span>LIVE</span>
            </Link>
            
            {/* Studio Musical - Nouveau service */}
            <Link
              to="/studio-musical"
              className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1 font-semibold"
            >
              <Music size={18} />
              <span>STUDIO MUSICAL</span>
            </Link>
            
            {/* À propos */}
            <Link
              to="/a-propos"
              className="text-white hover:text-[#E50914] transition-colors font-semibold"
            >
              À PROPOS
            </Link>
            
            {/* Services Boost - Accessible à TOUS */}
            <Link
              to="/services-boost"
              className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1 font-semibold"
            >
              <span>🚀</span>
              <span>SERVICES BOOST</span>
            </Link>
            
            {user?.role === 'super_admin' && (
              <Link
                to="/admin-panel"
                className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1"
              >
                <Shield size={18} />
                <span>Admin Panel</span>
              </Link>
            )}
          </nav>

          {/* User menu */}
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Bouton Vendez vidéo sur AGRP Box */}
                <Link
                  to="/vendre-videos"
                  className="text-white hover:text-[#E50914] transition-colors flex items-center space-x-1"
                >
                  <Video size={20} />
                  <span className="hidden lg:inline">Vendez vidéo sur AGRP Box</span>
                </Link>
                
                {/* Notifications */}
                <div className="text-white">
                  <NotificationBell />
                </div>
                
                {/* Bouton Publier - Accessible à TOUS */}
                <Link 
                  to="/mon-espace-artiste"
                  className="hidden sm:block"
                >
                  <Button className="bg-gradient-to-r from-[#E50914] to-[#F97316] hover:from-[#F8212C] hover:to-[#FB923C] text-white font-semibold shadow-lg">
                    📤 PUBLIER
                  </Button>
                </Link>
                
                <Link to="/purchases" className="hidden sm:block text-white hover:text-[#E50914] transition-colors">
                  Mes achats
                </Link>
                
                {/* Menu déroulant sur la photo de profil */}
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-[#E50914] rounded-full p-1 hover:bg-white/10 transition-all">
                      {user.profile_photo_path ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${user.profile_photo_path}`}
                          alt={user.full_name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-[#E50914] cursor-pointer"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#E50914] flex items-center justify-center cursor-pointer">
                          <User size={20} className="text-white" />
                        </div>
                      )}
                      <span className="hidden lg:block text-white font-medium">{user.full_name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-zinc-900 border-zinc-700" align="end" sideOffset={5}>
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user.full_name}</p>
                        <p className="text-xs text-zinc-400">{user.email}</p>
                        {user.role === 'super_admin' && (
                          <span className="text-xs bg-[#E50914] text-white px-2 py-0.5 rounded w-fit">Admin</span>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-zinc-700" />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-white hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer flex items-center space-x-2"
                    >
                      <LogOut size={16} />
                      <span>Se déconnecter</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="bg-[#E50914] hover:bg-[#F8212C] text-white"
              >
                Connexion
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;